/**
 * Per-day segment extraction for calendar events.
 *
 * Given a CalendarItem and a single day's bounds, returns the portion of
 * the event that should render on that day, or null if the event does not
 * touch that day at all. Renderers call this once per (event, day) pair
 * and use the returned segStartMs / segEndMs as the block's vertical
 * positioning inputs — the original event id / payload are preserved by
 * the caller so click-through still opens the full record.
 *
 * Data model:
 *   - Multi-day events: event.workDays is a non-empty array of
 *     { startMs, endMs } entries, one per worked day with concrete per-day
 *     times. Each entry's startMs falls on the day it belongs to.
 *   - Single-day / legacy events: event.workDays is empty or missing.
 *     Parent event.startMs / event.endMs are the source of truth. Legacy
 *     records without endMs fall back to a 1-hour default to match the
 *     existing render fallback elsewhere in the client.
 *
 * Non-contiguous schedules (Mon/Tue/Fri) and weekend-skipping schedules
 * (Thu→Mon) are handled naturally: a day with no matching workDays entry
 * returns null and no block is drawn.
 */

const ONE_HOUR_MS = 3600000;

function toMs(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.length > 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * @param {object} event      CalendarItem with optional workDays array.
 * @param {number|string} dayStartMs  Inclusive lower bound of the target day.
 * @param {number|string} dayEndMs    Inclusive upper bound of the target day.
 * @returns {{
 *   segStartMs: string,
 *   segEndMs: string,
 *   workDayIndex: number,
 *   totalWorkDays: number,
 *   isFirstDay: boolean,
 *   isLastDay: boolean,
 * } | null}
 *
 * For single-day / legacy events, `totalWorkDays === 1` and both
 * `isFirstDay` and `isLastDay` are true. Callers should check
 * `totalWorkDays > 1` to decide whether to render multi-day chevrons.
 */
export function splitEventForDay(event, dayStartMs, dayEndMs) {
  if (!event) return null;
  const dayStart = toMs(dayStartMs);
  const dayEnd = toMs(dayEndMs);
  if (dayStart === null || dayEnd === null) return null;

  const workDays = Array.isArray(event.workDays) ? event.workDays : [];

  if (workDays.length > 0) {
    for (let i = 0; i < workDays.length; i++) {
      const entry = workDays[i];
      const s = toMs(entry?.startMs);
      const e = toMs(entry?.endMs);
      if (s === null || e === null) continue;
      if (s >= dayStart && s <= dayEnd) {
        return {
          segStartMs: String(s),
          segEndMs: String(e),
          workDayIndex: i,
          totalWorkDays: workDays.length,
          isFirstDay: i === 0,
          isLastDay: i === workDays.length - 1,
        };
      }
    }
    return null;
  }

  const s = toMs(event.startMs);
  if (s === null) return null;
  if (s < dayStart || s > dayEnd) return null;
  const e = toMs(event.endMs);
  const endMs = e !== null ? e : s + ONE_HOUR_MS;
  return {
    segStartMs: String(s),
    segEndMs: String(endMs),
    workDayIndex: 0,
    totalWorkDays: 1,
    isFirstDay: true,
    isLastDay: true,
  };
}

export default splitEventForDay;
